import * as React from 'react';
import * as Theme from '@sail/theme-classic';
import {css} from '../css';
import {Action} from './Action';
import type {ActionProps} from './Action';

const styles = css({
  stack: 'x',
  display: css.value('inline-flex'),
  alignX: 'center',
  alignY: 'center',
  whiteSpace: css.value('nowrap'),
});

export type ButtonProps = {
  /**
   * The size of the button
   * @external
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * The type of the button
   * @external
   */
  type?: 'primary' | 'secondary' | 'destructive';

  /**
   * Allows the button to submit or reset a form
   * @external
   */
  behavior?: 'submit' | 'reset';

  /**
   * The id of the form element to associate the button with (if it's not an ancestor of the button)
   */
  form?: string;
} & ActionProps;

const buttonSizeToIconSizeMap = {
  small: 'xsmall' as const,
  medium: 'xsmall' as const,
  large: 'small' as const,
};

/**
 * @external
 * @param autofocus
 * @param className
 * @param disabled
 * @param form
 * @param name
 * @param onPress
 * @param type
 * @param value
 * @param target
 */
export const Button = Action.extend<ButtonProps>(
  ({size = 'medium', type = 'secondary', behavior, form, ...props}, View) => {
    return (
      <View
        {...props}
        uses={[
          styles,
          Theme.ButtonVariants.assign({size, type}),
          Theme.Button,
          Theme.IconVariants.internal_setAdaptiveDefaults({
            size: buttonSizeToIconSizeMap[size],
          }),
          behavior
            ? {
                onPress: (e) => {
                  const button = Object.assign(
                    document.createElement('button'),
                    {
                      type: behavior,
                      hidden: true,
                      'aria-hidden': 'true',
                    },
                  );
                  form && button.setAttribute('form', form);
                  e.target.parentNode?.appendChild(button);
                  button.click();
                  button.remove();
                },
              }
            : null,
        ]}
      />
    );
  },
);

Button.displayName = 'Button';
